import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { LoadCurrenciesAction } from '@core/store/payments/invoice-payments.actions';
import { InvoicesState } from '@core/store/payments/invoice-payments.state';
import {
  NgbTypeahead,
  NgbTypeaheadModule,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-currencies-typeahead',
  imports: [NgbTypeaheadModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './currencies-typeahead.component.html',
})
export class CurrenciesTypeaheadComponent implements OnInit {
  private store = inject(Store);

  @ViewChild('instance', { static: true }) instance: NgbTypeahead;
  @ViewChild('htmlTypeaheadElement', { static: true })
  htmlTypeaheadElement: ElementRef;
  @Input({ required: true }) control: FormControl<string>;
  @Input() placeholder: string;

  protected currencies$: Observable<string[]>;
  protected focus$ = new Subject<string>();
  protected click$ = new Subject<string>();

  private lastSearchTerm = '';

  ngOnInit(): void {
    this.currencies$ = this.store
      .dispatch(new LoadCurrenciesAction())
      .pipe(switchMap(() => this.store.select(InvoicesState.getCurrencies)));

    if (this.control) {
      this.htmlTypeaheadElement.nativeElement.value = this.control.value;
      this.lastSearchTerm = this.control.value;
    }
  }

  protected searchCurrencies = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      tap((term) => {
        if (this.lastSearchTerm !== term) {
          this.control.setValue(null);
        }

        this.lastSearchTerm = term;
      }),
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => (this.instance ? !this.instance.isPopupOpen() : false))
    );
    const inputFocus$ = this.focus$;

    return this.currencies$.pipe(
      switchMap((currencies) =>
        merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
          map((term) =>
            term === ''
              ? currencies
              : currencies.filter(
                  (v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1
                )
          )
        )
      )
    );
  };

  protected selectItem(selectItemEvent: NgbTypeaheadSelectItemEvent) {
    this.control.setValue(selectItemEvent.item);
  }
}
